import type { ReactNode } from "react";
import { useId, useRef, useState } from "react";
import MoreHorizRounded from "@mui/icons-material/MoreHorizRounded";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import { userApi } from "@bw/shared/api";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@bw/shared/hooks";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import { Page } from "../../../common/components/Page";
import { ReportDialog } from "../../../common/components/ReportDialog";
import { BlockPlayerButton } from "../../../common/components/BlockPlayerButton";
import { FollowPlayerButton } from "../../../common/components/FollowPlayerButton";
import { useBlockStatus } from "../../../common/hooks/useBlockPlayer";
import { AchievementList } from "../components/AchievementList";
import { PlayerPostGallery } from "../components/PlayerPostGallery";
import { PlayerProfileView } from "../components/PlayerProfileView";
import { useAchievements } from "../hooks/useAchievements";
import { useHeadToHead } from "../hooks/useHeadToHead";
import { readSearchReturn } from "../../../common/searchNavigation";
import { PublicGamificationFrame } from "../../gamification";

export interface PlayerProfilePageProps {
  /**
   * App-injected friendship actions (Add friend / Accept / Message) for another
   * player. Friendship belongs to Social; this page only places the control.
   */
  renderFriendActions?: (playerId: string, playerName: string) => ReactNode;
}

const actionSx = { borderRadius: 999, flex: { xs: 1, sm: "0 0 auto" } } as const;

function OwnProfileActions({ userId }: { userId: string }) {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = `${window.location.origin}/players/${encodeURIComponent(userId)}`;
    try {
      if (navigator.share) await navigator.share({ url });
      else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      }
    } catch {
      // The person closed the share sheet; nothing to report.
    }
  };
  return (
    <Stack direction="row" spacing={1}>
      <Button component={RouterLink} to="/me/edit" variant="contained" sx={actionSx}>
        Edit profile
      </Button>
      <Button variant="outlined" color="inherit" onClick={() => void share()} sx={actionSx}>
        {copied ? "Link copied" : "Share profile"}
      </Button>
    </Stack>
  );
}

function OtherProfileActions({
  userId,
  name,
  canInteract,
  friendActions,
}: {
  userId: string;
  name: string;
  canInteract: boolean;
  friendActions: ReactNode;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
      {/* Phone: actions fill the row; wider screens keep them beside ⋯. */}
      <Box sx={{ flex: { xs: 1, sm: "0 1 auto" }, minWidth: 0 }}>{canInteract ? friendActions : null}</Box>
      <IconButton
        ref={moreRef}
        aria-label={`More options for ${name}`}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        aria-controls={anchor ? panelId : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ border: 1, borderColor: "divider" }}
      >
        <MoreHorizRounded />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            id: panelId,
            role: "dialog",
            "aria-label": `Options for ${name}`,
            sx: { p: 1.5, borderRadius: 3 },
          },
        }}
      >
        <Stack spacing={1}>
          {canInteract ? <FollowPlayerButton playerId={userId} playerName={name} /> : null}
          <BlockPlayerButton playerId={userId} playerName={name} />
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            onClick={() => {
              setAnchor(null);
              setReportOpen(true);
            }}
          >
            Report {name}
          </Button>
        </Stack>
      </Popover>
      <ReportDialog
        open={reportOpen}
        subjectLabel={name}
        targetId={userId}
        targetType="player"
        onClose={() => {
          setReportOpen(false);
          // Return focus to the control that opened the options.
          requestAnimationFrame(() => moreRef.current?.focus());
        }}
      />
    </Stack>
  );
}

function AuthorizedPlayerProfile({
  userId,
  displayName,
  searchReturn,
  renderFriendActions,
}: {
  userId: string;
  displayName: string;
  searchReturn?: string;
  renderFriendActions?: PlayerProfilePageProps["renderFriendActions"];
}) {
  const { user } = useAuth();
  const myId = user?.uid;
  const isSelf = Boolean(userId) && userId === myId;
  const blockStatus = useBlockStatus(userId ?? "", !isSelf && Boolean(userId));
  const achievementsQ = useAchievements(userId);
  const headToHeadQ = useHeadToHead(
    isSelf ? undefined : myId,
    isSelf ? undefined : userId,
  );
  const name = displayName.trim() ? displayName : "—";
  const canInteract = Boolean(
    !isSelf &&
      blockStatus.data &&
      !blockStatus.data.is_blocked &&
      !blockStatus.data.is_blocked_by,
  );

  return (
    <Page title="Player profile" hideTitle>
      <Stack spacing={2}>
        {searchReturn ? (
          <Button
            component={RouterLink}
            to={searchReturn}
            sx={{ alignSelf: "flex-start" }}
          >
            Back to search
          </Button>
        ) : null}
        <PublicGamificationFrame userId={userId}>
          <PlayerProfileView
            userId={userId}
            headToHead={headToHeadQ.data}
            actions={
              isSelf ? (
                <OwnProfileActions userId={userId} />
              ) : (
                <OtherProfileActions
                  userId={userId}
                  name={name}
                  canInteract={canInteract}
                  friendActions={renderFriendActions?.(userId, name)}
                />
              )
            }
            posts={
              <Stack spacing={2.5}>
                {achievementsQ.data?.length ? (
                  <AchievementList achievements={achievementsQ.data} />
                ) : null}
                <PlayerPostGallery userId={userId} />
              </Stack>
            }
          />
        </PublicGamificationFrame>
      </Stack>
    </Page>
  );
}

export function PlayerProfilePage({ renderFriendActions }: PlayerProfilePageProps = {}) {
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const searchReturn = readSearchReturn(location.search);
  const { user } = useAuth();
  const profile = useQuery({
    queryKey: ["performance", "identity", user?.uid, userId],
    queryFn: () => userApi.getProfile(userId!),
    enabled: Boolean(user && userId),
    retry: false,
  });
  if (!user || !userId || profile.isLoading)
    return (
      <Page title="Player profile">
        <Typography role="status">Loading profile…</Typography>
      </Page>
    );
  if (profile.isError || !profile.data)
    return (
      <Page title="Player profile">
        <Alert severity="info">
          This profile isn&apos;t available. The player may keep it private.
        </Alert>
      </Page>
    );
  return (
    <AuthorizedPlayerProfile
      key={userId}
      userId={userId}
      displayName={profile.data.display_name}
      searchReturn={searchReturn}
      renderFriendActions={renderFriendActions}
    />
  );
}
